import styled from '@emotion/styled';
import type React from 'react';

const ShoppingBehaviorSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px 40px;
  margin-left: 60px;
  margin-right: 60px;
  margin-bottom: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #e8e8e8;

  h2 {
    margin: 0 0 8px 0;
    color: #333;
    font-size: 1.8em;
    font-weight: 700;
  }

  .subtitle {
    margin: 0 0 30px 0;
    color: #777;
    font-size: 1em;
    line-height: 1.4;
  }

  .products-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 40px;
    width: 100%;

    @media (max-width: 1200px) {
      grid-template-columns: 1fr 1fr;
      gap: 30px;
    }

    @media (max-width: 800px) {
      grid-template-columns: 1fr;
      gap: 20px;
    }
  }
`;

const ProductColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const ProductList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const ProductItem = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  font-size: 0.9em;
  line-height: 1.4;

  .product-name {
    color: #333;
    font-weight: 400;
    flex: 1;
    margin-right: 16px;
  }

  .product-percentage {
    color: #666;
    font-weight: 500;
    text-align: right;
    white-space: nowrap;
  }
`;

const ProductNumber = styled.span`
  color: #bbb;
  font-size: 1em;
  margin-right: 8px;
  font-weight: 400;
`;

type BrandProduct = {
  product_category: string;
  percentage_of_buyers: number;
};

interface ShoppingBehaviorProps {
  brandProducts: Record<string, unknown>[];
}

const ShoppingBehavior: React.FC<ShoppingBehaviorProps> = ({ brandProducts }) => {
  if (!brandProducts || brandProducts.length === 0) {
    return null;
  }

  const products = brandProducts as BrandProduct[];

  // Split products into three columns
  const itemsPerColumn = Math.ceil(products.length / 3);
  const column1 = products.slice(0, itemsPerColumn);
  const column2 = products.slice(itemsPerColumn, itemsPerColumn * 2);
  const column3 = products.slice(itemsPerColumn * 2);

  const renderProductColumn = (columnProducts: BrandProduct[], startIndex: number) => (
    <ProductColumn>
      <ProductList>
        {columnProducts.map((item, index) => {
          const globalIndex = startIndex + index;
          return (
            <ProductItem key={`${item.product_category}-${globalIndex}`}>
              <div>
                <ProductNumber>{globalIndex + 1}.</ProductNumber>
                <span className="product-name">{item.product_category}</span>
              </div>
              <span className="product-percentage">{item.percentage_of_buyers.toFixed(1)}%</span>
            </ProductItem>
          );
        })}
      </ProductList>
    </ProductColumn>
  );

  return (
    <ShoppingBehaviorSection>
      <h2>Shopping Behavior - Top Products</h2>
      <p className="subtitle">
        Your customers&apos; product level shopping activity across other brands.
      </p>

      <div className="products-grid">
        {renderProductColumn(column1, 0)}
        {renderProductColumn(column2, itemsPerColumn)}
        {renderProductColumn(column3, itemsPerColumn * 2)}
      </div>
    </ShoppingBehaviorSection>
  );
};

export default ShoppingBehavior;
